import React from "react";
import {
    EmailSubsectionChip, IconBuildingDocument, IconDoor, IconReload, MobileIcon
} from '../../../components/buttons/IconButtons';
import CustomTooltip from '../../../components/CustomTooltip';
import CustomChip from '../../../components/CustomChip';
import { statusBorderColor, statusColor, statusFontColor } from '../../../enums/statusColorEnum';
import { status } from '../../../enums/statusEnum';
import { EMPTY_VALUES } from '../../../Apis/functions';
import { RESIDENT_TYPE, RESIDENT_TYPE_CHIP } from "../../Building/Model/Model";
import CustomButtonLink from "../../../components/buttons/CustomButtonLink";

/**
 * Information card of the building the logged resident lives in: name and
 * apartment, location and the contact data of the main resident.
 *
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * @param {Object} props - Component properties
 * @param {Function} props.t - Translation function
 * @param {Object} props.buildingDetail - Building of the resident
 * @param {string} props.buildingDetail.buildingName - Name of the building
 * @param {string} props.buildingDetail.apartmentName - Apartment assigned to the resident
 * @param {string} props.buildingDetail.location - Address of the building
 * @param {string} props.buildingDetail.mainResident - Full name of the owner of the unit
 * @param {string} props.buildingDetail.email - Email of the main resident
 * @param {string} props.buildingDetail.phoneNumber - Phone number of the main resident
 * @param {number} props.buildingDetail.statusId - Status rendered as a chip on the header
 * @param {string|number} props.containerHeight - Height shared by the columns of the detail
 * @returns {JSX.Element}
 */
const MyApartmentInformation = ({
    t,
    buildingDetail,
    containerHeight
}) => {
    const statusId = buildingDetail?.statusId;
    // Same owner chip the household member cards paint next to the main resident
    const ownerChip = RESIDENT_TYPE_CHIP[RESIDENT_TYPE.OWNER];
    const {latitude, longitude} = buildingDetail || {};
    // The address only opens the map when the building has coordinates
    const hasCoordinates = !!latitude && !!longitude;

    /**
     * Opens the position of the building in google maps.
     *
     * @function openInMaps
     */
    const openInMaps = () => {
        if (!hasCoordinates) return;

        window.open(`https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`, '_blank');
    };

    return (
        <div className="border-full-shades-300 border-radius-8px d-flex flex-column overflow-hidden minh-645px" style={{ height: containerHeight }}>
            <div className='d-flex justify-content-between align-items-center p-16px'>
                <div className='d-flex align-items-center minw-0 mr-2'>
                    <div className={"d-flex align-items-center flex-shrink-0 mr-8px"}><IconBuildingDocument /></div>
                    <div className={"minw-0"}>
                        <CustomTooltip
                            text={t("key_details")}
                            classes={'font-color-general-950 ibl-font-w-600 fs-16px text-ellipsis-1-row'}
                            showIfTruncated={true}
                            placement={'top'}
                        />
                    </div>
                </div>
                <div className={"flex-shrink-0"}>
                    <CustomChip
                        background={statusColor[statusId]}
                        fontColor={statusFontColor[statusId]}
                        borderColor={statusBorderColor[statusId]}
                        legend={t(status[statusId]) || EMPTY_VALUES.DEFAULT}
                        variant={'large'}
                    />
                </div>
            </div>

            <div className='border-bottom-shades-300' />

            <div className='d-flex align-items-start p-3'>
                <div className='background-success-100 p-8px border-radius-8px d-flex justify-content-center flex-shrink-0 mr-3'>
                    <IconDoor width={30} height={30}/>
                </div>

                <div className='flex-grow-1 minw-0'>
                    <CustomTooltip
                        text={buildingDetail?.buildingName || EMPTY_VALUES.DEFAULT}
                        classes={'font-color-general-950 ibl-font-w-600 fs-14px text-ellipsis-1-row p-0 mb-2'}
                        showIfTruncated={true}
                        placement={'top'}
                        tooltipClasses={"p-0"}
                    />

                    <CustomTooltip
                        text={buildingDetail?.apartmentName || EMPTY_VALUES.DEFAULT}
                        classes={'fs-14px font-color-general-950 text-ellipsis-1-row p-0'}
                        showIfTruncated={true}
                        tooltipClasses={"p-0"}
                    />

                    <div className='border-bottom-shades-300 my-3' />

                    <p className='fs-14px ibl-font-w-600 p-0 mb-2'>{t('location')}</p>
                    <div
                        id={"lbl_mai_location"}
                        className={hasCoordinates ? "cursor-pointer" : ""}
                        onClick={openInMaps}
                    >
                        <CustomTooltip
                            text={buildingDetail?.location || EMPTY_VALUES.DEFAULT}
                            classes={`fs-14px text-ellipsis-2-rows p-0 ${hasCoordinates ? "font-color-primary-700 font-hover-primary-900" : "font-color-general-950"}`}
                            showIfTruncated={true}
                            tooltipClasses={"p-0"}
                        />
                    </div>

                    <div className='border-bottom-shades-300 my-3' />

                    <p className='fs-14px ibl-font-w-600 p-0 mb-2'>{t('main_resident')}</p>
                    <div className={"d-flex align-items-center justify-content-start"}>
                        <div className={"minw-0 mr-2"}>
                            <CustomTooltip
                                text={buildingDetail?.mainResident || EMPTY_VALUES.DEFAULT}
                                classes={'fs-14px font-color-general-950 text-ellipsis-1-row p-0'}
                                showIfTruncated={true}
                                tooltipClasses={"p-0"}
                            />

                        </div>
                        <div className={"flex-shrink-0"}>
                            <CustomChip
                                id={"chp_mbi_owner"}
                                background={ownerChip?.background}
                                fontColor={ownerChip?.fontColor}
                                borderColor={ownerChip?.borderColor}
                                legend={t(ownerChip?.translation)}
                                variant={'compact'}
                                icon={ownerChip?.icon}
                                classNameIcon={ownerChip?.classNameIcon}
                            />
                        </div>
                    </div>

                    <div className={"d-flex align-items-center justify-content-between mt-2"}>
                        <div className={"col-6 d-flex align-items-center pl-0"}>
                            <div className={"d-flex align-items-center flex-shrink-0 mr-8px"}><EmailSubsectionChip /></div>
                            <div className={"minw-0"}>
                                <CustomTooltip
                                    text={buildingDetail?.email || EMPTY_VALUES.DEFAULT}
                                    classes={'fs-14px font-color-general-950 text-ellipsis-1-row p-0'}
                                    showIfTruncated={true}
                                    tooltipClasses={"p-0"}
                                />
                            </div>
                        </div>

                        <div className={"col-6 d-flex align-items-center px-0"}>
                            <div className={"d-flex align-items-center flex-shrink-0 mr-8px"}><MobileIcon /></div>
                            <div className={"minw-0"}>
                                <CustomTooltip
                                    text={buildingDetail?.phoneNumber || EMPTY_VALUES.DEFAULT}
                                    classes={'fs-14px font-color-general-950 text-ellipsis-1-row p-0'}
                                    showIfTruncated={true}
                                    tooltipClasses={"p-0"}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyApartmentInformation;
